import {
  Button,
  Grid,
  Menu,
  MenuItem,
  Modal,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { paths } from 'src/paths';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SignModalIcon from 'src/icons/untitled-ui/duocolor/sign-modal-icon';
import { modalStyle } from 'src/utils/common-properties';
import SelectDor from './select-dor';
import { useDispatch, useSelector } from 'react-redux';
import { getMdsFormByIdAction } from 'src/redux/actions/mdsCommunicationActions';
import loader from 'src/components/LoaderImg/loader.gif';
import useCurrentUser from 'src/hooks/use-current-user';
import useCheckRole from 'src/hooks/use-check-role';

function ViewMdsCommunication() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { patients, patientDetails, mdsCommunication, signMds } = paths;
  const navigate = useNavigate();
  const { patientId, patientData, mdsData } = location.state;
  const [triggerSelectDOR, setTriggerDOR] = useState(false);
  const checkMdsDataById = useSelector((state: any) => state.getMdsformByIdReducer);
  const [providerId] = useCurrentUser(['providerId']);
  const [isDor] = useCheckRole('dor');

  const gotoback = () => {
    navigate(`${patients}/${patientDetails}${mdsCommunication}`, {
      state: {
        patientId,
        patientData,
      },
    });
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const openSelectDORModal = () => {
    setTriggerDOR(true);
    handleCloseMenu();
  };

  const callGetMdsForm = (uuid: string) => {
    dispatch(getMdsFormByIdAction(uuid));
  };

  const closeSelectDORModal = (action = '') => {
    if (action === '') {
      callGetMdsForm(mdsData?.uuid);
    }
    setTriggerDOR(false);
  };

  useEffect(() => {
    if (!mdsData || !mdsData?.uuid) return;
    callGetMdsForm(mdsData?.uuid);
  }, [mdsData]);

  const verifyTheCorrectDor = () => {
    return providerId === checkMdsDataById?.response?.data?.data?.signerId;
  };

  const redirectToSignaturePage = () => {
    navigate(`${patients}/${patientDetails}${mdsCommunication}${signMds}`, {
      state: {
        patientId,
        patientData,
        mdsData: checkMdsDataById?.response?.data?.data,
      },
    });
  };

  return (
    <>
      <Grid
        container
        justifyContent={'space-between'}
        mt={2}
      >
        <Stack
          gap="1.5rem"
          sx={{ padding: '0 4rem' }}
        >
          <Button
            sx={{
              justifyContent: 'flex-start',
              color: '#111927',
              width: 'max-content',
              padding: '0px',
            }}
            onClick={gotoback}
            startIcon={
              <SvgIcon>
                <ArrowBackIcon sx={{ color: '#6C737F' }} />
              </SvgIcon>
            }
          >
            <Typography variant="subtitle1">Back to MDS Assessment List</Typography>
          </Button>
          {checkMdsDataById.loading ? (
            <Grid>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </Grid>
          ) : (
            <Stack gap="1rem">
              <Typography variant="h5">View Document - MDS Assessment</Typography>
              {checkMdsDataById?.response?.data?.data?.signerName && (
                <Typography variant="body1">
                  Selected DOR - {checkMdsDataById?.response?.data?.data?.signerName}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
        <Stack mr={3}>
          <Button
            variant="contained"
            endIcon={
              <SvgIcon>
                <ExpandMoreIcon />
              </SvgIcon>
            }
            id="basic-button"
            aria-haspopup="true"
            onClick={handleClick}
          >
            Actions
          </Button>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <Tooltip
              title={
                checkMdsDataById?.response?.data?.data?.signerId
                  ? 'DOR has been already assigned'
                  : 'Only DOR can select a DOR'
              }
              followCursor
            >
              <span>
                <MenuItem
                  onClick={openSelectDORModal}
                  disabled={checkMdsDataById?.response?.data?.data?.signerId || !isDor}
                >
                  <Grid
                    container
                    alignItems={'center'}
                    gap={1}
                  >
                    <AssignmentTurnedInIcon color="primary" />
                    <Grid item>Select DOR</Grid>
                  </Grid>
                </MenuItem>
              </span>
            </Tooltip>

            <Tooltip
              title={
                mdsData?.status === 'FINALIZED'
                  ? 'MDS document has been signed'
                  : verifyTheCorrectDor()
                  ? ''
                  : 'Only selected DOR can sign a document'
              }
              followCursor
            >
              <span>
                <MenuItem
                  disabled={!verifyTheCorrectDor() || mdsData?.status === 'FINALIZED'}
                  onClick={redirectToSignaturePage}
                >
                  <Grid
                    container
                    alignItems={'center'}
                    gap={1}
                  >
                    <SvgIcon>
                      <SignModalIcon />
                    </SvgIcon>
                    <Grid item>Sign a document</Grid>
                  </Grid>
                </MenuItem>
              </span>
            </Tooltip>
          </Menu>
        </Stack>
      </Grid>
      {checkMdsDataById.loading ? (
        <img
          src={loader}
          alt="loader"
          style={{ width: '10%', margin: 'auto' }}
        />
      ) : (
        <Box
          sx={{ height: '100%' }}
          p={3}
        >
          <iframe
            src={checkMdsDataById?.response?.data?.data?.documentKey}
            width={'100%'}
            height={'100%'}
            style={{ border: 'none' }}
          />
        </Box>
      )}

      <Modal
        style={modalStyle}
        open={triggerSelectDOR}
        onClose={() => closeSelectDORModal('closeModal')}
      >
        <div>
          <SelectDor
            mdsFormId={checkMdsDataById?.response?.data?.data?.uuid as string}
            close={closeSelectDORModal}
          />
        </div>
      </Modal>
    </>
  );
}

export default ViewMdsCommunication;
